import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCropAlt,
  faRotateRight,
  faPen,
  faSquare,
  faThLarge,
  faSmile,
  faUndo,
  faDownload,
  faCopy,
  faPaperPlane,
  faCheck,
  faPlus,
  faWandMagicSparkles,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import EmojiPickerPopover from './EmojiPickerPopover';

// Paleta de colores oficiales estilo WhatsApp Web
const WHATSAPP_COLORS = [
  { id: 'red', hex: '#ef4444', label: 'Rojo' },
  { id: 'orange', hex: '#f97316', label: 'Naranja' },
  { id: 'yellow', hex: '#eab308', label: 'Amarillo' },
  { id: 'green', hex: '#22c55e', label: 'Verde' },
  { id: 'whatsapp', hex: '#00a884', label: 'WhatsApp' },
  { id: 'cyan', hex: '#06b6d4', label: 'Cian' },
  { id: 'blue', hex: '#3b82f6', label: 'Azul' },
  { id: 'purple', hex: '#a855f7', label: 'Púrpura' },
  { id: 'white', hex: '#ffffff', label: 'Blanco' },
  { id: 'black', hex: '#000000', label: 'Negro' }
];

const STROKE_WIDTHS = [
  { id: 'fine', size: 3, label: 'Fino' },
  { id: 'medium', size: 6, label: 'Medio' },
  { id: 'thick', size: 12, label: 'Grueso' },
  { id: 'xl', size: 20, label: 'Extra' }
];

const FONT_SIZES = [
  { id: 's', size: 28, label: 'S' },
  { id: 'm', size: 42, label: 'M' },
  { id: 'l', size: 60, label: 'L' }
];

const FILTERS = [
  { id: 'normal', label: 'Normal' },
  { id: 'grayscale', label: 'B/N' },
  { id: 'sepia', label: 'Sepia' },
  { id: 'invert', label: 'Invertido' },
  { id: 'contrast', label: 'Vívido' }
];

const ImageEditorModal = ({
  isOpen,
  filePreview,
  inputText = '',
  setInputText,
  onSave,
  onSend,
  onDiscard,
  onClose,
  onAddFile
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const captionInputRef = useRef(null);

  // Herramienta activa: 'pen' por defecto para que las herramientas de dibujo y colores estén siempre visibles
  const [activeTool, setActiveTool] = useState('pen');
  const [strokeColor, setStrokeColor] = useState('#00a884');
  const [strokeWidth, setStrokeWidth] = useState(6);
  const [fontSize, setFontSize] = useState(42);
  const [isDrawing, setIsDrawing] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Historial de cambios
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Pie de foto
  const [caption, setCaption] = useState(inputText || '');

  // Recorte
  const [cropSelection, setCropSelection] = useState(null);
  const [cropDragStart, setCropDragStart] = useState(null);

  // Pixelado / Censura
  const [pixelateSelection, setPixelateSelection] = useState(null);
  const [pixelateDragStart, setPixelateDragStart] = useState(null);

  // Formas
  const [shapeSelection, setShapeSelection] = useState(null);
  const [shapeDragStart, setShapeDragStart] = useState(null);
  const [shapeFilled, setShapeFilled] = useState(false);

  // Texto
  const [textValue, setTextValue] = useState('');

  // Confirmación de descarte
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Notificaciones rápidas
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showEmojiTray, setShowEmojiTray] = useState(false);

  // Sincronizar caption
  useEffect(() => {
    if (inputText) {
      setCaption(inputText);
    }
  }, [inputText]);

  // Cargar imagen en el Canvas
  useEffect(() => {
    if (!isOpen || !filePreview?.url) return;
    let isMounted = true;
    setImageLoaded(false);

    const img = new Image();
    // Solo aplicar crossOrigin para URLs remotas; nunca para blob: ni data:
    if (filePreview.url.startsWith('http://') || filePreview.url.startsWith('https://')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      if (!isMounted) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth || 800;
      canvas.height = img.naturalHeight || 600;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const initialData = canvas.toDataURL('image/png');
      setHistory([initialData]);
      setHistoryIndex(0);
      setCropSelection(null);
      setPixelateSelection(null);
      setShapeSelection(null);
      setShowDiscardConfirm(false);
      setImageLoaded(true);
    };

    img.onerror = () => {
      // Reintentar sin crossOrigin si falló
      if (img.crossOrigin) {
        const retryImg = new Image();
        retryImg.onload = () => {
          if (!isMounted) return;
          const canvas = canvasRef.current;
          if (!canvas) return;
          canvas.width = retryImg.naturalWidth || 800;
          canvas.height = retryImg.naturalHeight || 600;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(retryImg, 0, 0);
          setHistory([canvas.toDataURL('image/png')]);
          setHistoryIndex(0);
          setImageLoaded(true);
        };
        retryImg.src = filePreview.url;
      }
    };

    img.src = filePreview.url;

    return () => {
      isMounted = false;
    };
  }, [isOpen, filePreview?.url]);

  // Guardar estado en el historial
  const pushHistoryState = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL('image/png');
    setHistory(prev => [...prev.slice(0, historyIndex + 1), data]);
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // Deshacer acción
  const handleUndo = () => {
    if (historyIndex <= 0) return;
    const targetIndex = historyIndex - 1;
    const targetData = history[targetIndex];

    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      setHistoryIndex(targetIndex);
      setCropSelection(null);
      setPixelateSelection(null);
      setShapeSelection(null);
    };
    img.src = targetData;
  };

  // Rotar 90 grados
  const handleRotate90 = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.height;
    tempCanvas.height = canvas.width;
    const tempCtx = tempCanvas.getContext('2d');

    tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
    tempCtx.rotate((90 * Math.PI) / 180);
    tempCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

    canvas.width = tempCanvas.width;
    canvas.height = tempCanvas.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(tempCanvas, 0, 0);
    setCropSelection(null);
    pushHistoryState();
  };

  // Convertir coordenadas del cursor a coordenadas reales del canvas
  const getCanvasCoords = e => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX !== undefined ? e.clientX : (e.touches?.[0]?.clientX || 0);
    const clientY = e.clientY !== undefined ? e.clientY : (e.touches?.[0]?.clientY || 0);
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: Math.max(0, Math.min(canvas.width, (clientX - rect.left) * scaleX)),
      y: Math.max(0, Math.min(canvas.height, (clientY - rect.top) * scaleY))
    };
  };

  // Aplicar Pixelado / Difuminado en una zona seleccionada
  const applyPixelateRect = (x, y, w, h, pixelSize = 14) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const rx = Math.max(0, Math.min(canvas.width, Math.round(x)));
    const ry = Math.max(0, Math.min(canvas.height, Math.round(y)));
    const rw = Math.max(1, Math.min(canvas.width - rx, Math.round(w)));
    const rh = Math.max(1, Math.min(canvas.height - ry, Math.round(h)));

    if (rw < 5 || rh < 5) return;

    const offCanvas = document.createElement('canvas');
    const offCtx = offCanvas.getContext('2d');
    const scaledW = Math.max(1, Math.round(rw / pixelSize));
    const scaledH = Math.max(1, Math.round(rh / pixelSize));
    offCanvas.width = scaledW;
    offCanvas.height = scaledH;

    offCtx.drawImage(canvas, rx, ry, rw, rh, 0, 0, scaledW, scaledH);

    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(offCanvas, 0, 0, scaledW, scaledH, rx, ry, rw, rh);
    ctx.restore();

    pushHistoryState();
  };

  // Aplicar Filtro sobre toda la imagen
  const handleApplyFilter = filterType => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (filterType === 'normal') {
      if (history.length > 0) {
        const initialImg = new Image();
        initialImg.onload = () => {
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(initialImg, 0, 0);
          pushHistoryState();
        };
        initialImg.src = history[0];
      }
      return;
    }

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext('2d');

    if (filterType === 'grayscale') tempCtx.filter = 'grayscale(100%)';
    else if (filterType === 'sepia') tempCtx.filter = 'sepia(100%)';
    else if (filterType === 'invert') tempCtx.filter = 'invert(100%)';
    else if (filterType === 'contrast') tempCtx.filter = 'contrast(160%) brightness(105%)';

    tempCtx.drawImage(canvas, 0, 0);
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(tempCanvas, 0, 0);
    pushHistoryState();
  };

  // Estampar Texto en el centro de la imagen
  const handleStampText = () => {
    if (!textValue.trim()) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    ctx.save();
    ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    ctx.fillStyle = strokeColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.85)';
    ctx.shadowBlur = 8;
    ctx.lineWidth = Math.max(2, Math.round(fontSize / 14));
    ctx.strokeStyle = '#000000';

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    ctx.strokeText(textValue, cx, cy);
    ctx.fillText(textValue, cx, cy);
    ctx.restore();

    setTextValue('');
    pushHistoryState();
  };

  // Eventos de ratón / táctil
  const handleMouseDown = e => {
    const coords = getCanvasCoords(e);

    if (activeTool === 'crop') {
      setCropDragStart(coords);
      setCropSelection({ x: coords.x, y: coords.y, width: 0, height: 0 });
      return;
    }

    if (activeTool === 'pixelate') {
      setPixelateDragStart(coords);
      setPixelateSelection({ x: coords.x, y: coords.y, width: 0, height: 0 });
      return;
    }

    if (activeTool === 'square') {
      setShapeDragStart(coords);
      setShapeSelection({ x: coords.x, y: coords.y, width: 0, height: 0 });
      return;
    }

    if (activeTool === 'pen') {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');

      setIsDrawing(true);
      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      ctx.globalAlpha = 1.0;
    }
  };

  const handleMouseMove = e => {
    const coords = getCanvasCoords(e);

    if (activeTool === 'crop' && cropDragStart) {
      const x = Math.min(cropDragStart.x, coords.x);
      const y = Math.min(cropDragStart.y, coords.y);
      const width = Math.abs(coords.x - cropDragStart.x);
      const height = Math.abs(coords.y - cropDragStart.y);
      setCropSelection({ x, y, width, height });
      return;
    }

    if (activeTool === 'pixelate' && pixelateDragStart) {
      const x = Math.min(pixelateDragStart.x, coords.x);
      const y = Math.min(pixelateDragStart.y, coords.y);
      const width = Math.abs(coords.x - pixelateDragStart.x);
      const height = Math.abs(coords.y - pixelateDragStart.y);
      setPixelateSelection({ x, y, width, height });
      return;
    }

    if (activeTool === 'square' && shapeDragStart) {
      const x = Math.min(shapeDragStart.x, coords.x);
      const y = Math.min(shapeDragStart.y, coords.y);
      const width = Math.abs(coords.x - shapeDragStart.x);
      const height = Math.abs(coords.y - shapeDragStart.y);
      setShapeSelection({ x, y, width, height });
      return;
    }

    if (activeTool === 'pen' && isDrawing) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
    }
  };

  const handleMouseUp = () => {
    if (activeTool === 'crop') {
      setCropDragStart(null);
      return;
    }

    if (activeTool === 'pixelate') {
      if (pixelateSelection && pixelateSelection.width > 5 && pixelateSelection.height > 5) {
        applyPixelateRect(
          pixelateSelection.x,
          pixelateSelection.y,
          pixelateSelection.width,
          pixelateSelection.height
        );
      }
      setPixelateDragStart(null);
      setPixelateSelection(null);
      return;
    }

    if (activeTool === 'square') {
      if (shapeSelection && shapeSelection.width > 5 && shapeSelection.height > 5) {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx.save();
          if (shapeFilled) {
            ctx.fillStyle = strokeColor;
            ctx.fillRect(shapeSelection.x, shapeSelection.y, shapeSelection.width, shapeSelection.height);
          } else {
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = strokeWidth;
            ctx.strokeRect(shapeSelection.x, shapeSelection.y, shapeSelection.width, shapeSelection.height);
          }
          ctx.restore();
          pushHistoryState();
        }
      }
      setShapeDragStart(null);
      setShapeSelection(null);
      return;
    }

    if (activeTool === 'pen' && isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.closePath();
        pushHistoryState();
      }
    }
  };

  // Aplicar Recorte
  const handleApplyCrop = () => {
    if (!cropSelection || cropSelection.width < 10 || cropSelection.height < 10) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = Math.round(cropSelection.width);
    tempCanvas.height = Math.round(cropSelection.height);
    const tempCtx = tempCanvas.getContext('2d');

    tempCtx.drawImage(
      canvas,
      cropSelection.x,
      cropSelection.y,
      cropSelection.width,
      cropSelection.height,
      0,
      0,
      tempCanvas.width,
      tempCanvas.height
    );

    canvas.width = tempCanvas.width;
    canvas.height = tempCanvas.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(tempCanvas, 0, 0);

    setCropSelection(null);
    setActiveTool('pen');
    pushHistoryState();
  };

  // Descargar imagen
  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const originalName = filePreview?.name || 'imagen.png';
    const cleanName = originalName.replace(/\.[^/.]+$/, '') + '_editada.png';
    const link = document.createElement('a');
    link.download = cleanName;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Copiar imagen
  const handleCopy = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      canvas.toBlob(async blob => {
        if (!blob) return;
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 2200);
      }, 'image/png');
    } catch (err) {
      console.warn('No se pudo copiar la imagen al portapapeles:', err);
    }
  };

  // Exportar datos
  const buildExportData = callback => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (activeTool === 'text' && textValue && textValue.trim()) {
      handleStampText();
    }
    if (activeTool === 'crop' && cropSelection && cropSelection.width >= 10 && cropSelection.height >= 10) {
      handleApplyCrop();
    }

    canvas.toBlob(blob => {
      if (!blob) return;
      const originalName = filePreview?.name || 'imagen_whatsapp.png';
      const cleanName = originalName.replace(/\.[^/.]+$/, '') + '_editada.png';
      const newFile = new File([blob], cleanName, { type: 'image/png' });
      const newUrl = URL.createObjectURL(blob);

      callback(newFile, {
        type: 'image',
        url: newUrl,
        name: cleanName,
        size: blob.size
      });
    }, 'image/png');
  };

  // Guardar cambios y volver al chat
  const handleSaveAndClose = () => {
    buildExportData((newFile, newPreview) => {
      if (setInputText && caption) {
        setInputText(caption);
      }
      if (onSave) {
        onSave(newFile, newPreview);
      } else if (onClose) {
        onClose();
      }
    });
  };

  // Enviar mensaje
  const handleSendAndClose = () => {
    buildExportData((newFile, newPreview) => {
      if (setInputText) {
        setInputText(caption);
      }
      if (onSend) {
        onSend(newFile, newPreview, caption);
      } else {
        if (onSave) onSave(newFile, newPreview);
        if (onClose) onClose();
      }
    });
  };

  // Intento de cerrar
  const handleAttemptClose = () => {
    const hasEdits = historyIndex > 0;
    const hasCaption = caption.trim().length > 0;

    if (hasEdits || hasCaption) {
      setShowDiscardConfirm(true);
    } else {
      handleConfirmDiscard();
    }
  };

  // Confirmar descarte
  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false);
    if (onDiscard) {
      onDiscard();
    } else if (onClose) {
      onClose();
    }
  };

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        if (showDiscardConfirm) {
          setShowDiscardConfirm(false);
        } else if (showEmojiTray) {
          setShowEmojiTray(false);
        } else {
          handleAttemptClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, showDiscardConfirm, showEmojiTray, historyIndex, caption]);

  if (!isOpen) return null;

  // Cálculo visual de recuadros
  const getScreenSelectionStyle = sel => {
    if (!sel || !canvasRef.current) return null;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width / canvas.width;
    const scaleY = rect.height / canvas.height;

    return {
      left: `${sel.x * scaleX}px`,
      top: `${sel.y * scaleY}px`,
      width: `${sel.width * scaleX}px`,
      height: `${sel.height * scaleY}px`
    };
  };

  // Renderizar a nivel de la pantalla del chat en #ticket-chat-main-column
  const chatContainer = typeof document !== 'undefined'
    ? (document.getElementById('ticket-chat-main-column') || document.body)
    : null;

  const isChatPane = Boolean(chatContainer && chatContainer.id === 'ticket-chat-main-column');

  const modalContent = (
    <div
      style={{
        position: isChatPane ? 'absolute' : 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: isChatPane ? '100%' : '100vw',
        height: isChatPane ? '100%' : '100vh',
        backgroundColor: '#0c1317',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        color: '#e9edef',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        userSelect: 'none',
        overflow: 'hidden'
      }}
    >
      {/* ── 1. Barra Superior Principal de WhatsApp Web ─────────────────────── */}
      <div
        style={{
          height: '60px',
          backgroundColor: '#111b21',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          flexShrink: 0
        }}
      >
        {/* Izquierda: Botón X (Cancelar / Cerrar) */}
        <button
          type="button"
          className="btn btn-link p-2 text-decoration-none shadow-none rounded-circle d-flex align-items-center justify-content-center"
          style={{
            color: '#aebac1',
            width: '40px',
            height: '40px',
            cursor: 'pointer'
          }}
          onClick={handleAttemptClose}
          title="Cerrar (Esc)"
        >
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: '1.25rem' }} />
        </button>

        {/* Centro: Iconos de herramientas estilo WhatsApp */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* 1. Recortar y Rotar */}
          <button
            type="button"
            className="btn btn-sm d-flex align-items-center justify-content-center shadow-none rounded-2 p-2"
            style={{
              width: '38px',
              height: '38px',
              backgroundColor: activeTool === 'crop' ? 'rgba(0, 168, 132, 0.25)' : 'transparent',
              color: activeTool === 'crop' ? '#00a884' : '#aebac1',
              cursor: 'pointer'
            }}
            onClick={() => setActiveTool(activeTool === 'crop' ? null : 'crop')}
            title="Recortar y rotar"
          >
            <FontAwesomeIcon icon={faCropAlt} style={{ fontSize: '1.1rem' }} />
          </button>

          {/* 2. Filtros */}
          <button
            type="button"
            className="btn btn-sm d-flex align-items-center justify-content-center shadow-none rounded-2 p-2"
            style={{
              width: '38px',
              height: '38px',
              backgroundColor: activeTool === 'filter' ? 'rgba(0, 168, 132, 0.25)' : 'transparent',
              color: activeTool === 'filter' ? '#00a884' : '#aebac1',
              cursor: 'pointer'
            }}
            onClick={() => setActiveTool(activeTool === 'filter' ? null : 'filter')}
            title="Filtros"
          >
            <FontAwesomeIcon icon={faWandMagicSparkles} style={{ fontSize: '1.05rem' }} />
          </button>

          {/* 3. Lápiz / Dibujar */}
          <button
            type="button"
            className="btn btn-sm d-flex align-items-center justify-content-center shadow-none rounded-2 p-2"
            style={{
              width: '38px',
              height: '38px',
              backgroundColor: activeTool === 'pen' ? 'rgba(0, 168, 132, 0.25)' : 'transparent',
              color: activeTool === 'pen' ? '#00a884' : '#aebac1',
              cursor: 'pointer'
            }}
            onClick={() => setActiveTool(activeTool === 'pen' ? null : 'pen')}
            title="Lápiz"
          >
            <FontAwesomeIcon icon={faPen} style={{ fontSize: '1.05rem' }} />
          </button>

          {/* 4. Texto 'Aa' */}
          <button
            type="button"
            className="btn btn-sm d-flex align-items-center justify-content-center shadow-none rounded-2 p-2 fw-bold"
            style={{
              width: '38px',
              height: '38px',
              fontSize: '1.12rem',
              backgroundColor: activeTool === 'text' ? 'rgba(0, 168, 132, 0.25)' : 'transparent',
              color: activeTool === 'text' ? '#00a884' : '#aebac1',
              cursor: 'pointer'
            }}
            onClick={() => setActiveTool(activeTool === 'text' ? null : 'text')}
            title="Texto"
          >
            Aa
          </button>

          {/* 5. Formas / Rectángulo */}
          <button
            type="button"
            className="btn btn-sm d-flex align-items-center justify-content-center shadow-none rounded-2 p-2"
            style={{
              width: '38px',
              height: '38px',
              backgroundColor: activeTool === 'square' ? 'rgba(0, 168, 132, 0.25)' : 'transparent',
              color: activeTool === 'square' ? '#00a884' : '#aebac1',
              cursor: 'pointer'
            }}
            onClick={() => setActiveTool(activeTool === 'square' ? null : 'square')}
            title="Formas"
          >
            <FontAwesomeIcon icon={faSquare} style={{ fontSize: '1.05rem' }} />
          </button>

          {/* 6. Difuminar / Pixelar */}
          <button
            type="button"
            className="btn btn-sm d-flex align-items-center justify-content-center shadow-none rounded-2 p-2"
            style={{
              width: '38px',
              height: '38px',
              backgroundColor: activeTool === 'pixelate' ? 'rgba(0, 168, 132, 0.25)' : 'transparent',
              color: activeTool === 'pixelate' ? '#00a884' : '#aebac1',
              cursor: 'pointer'
            }}
            onClick={() => setActiveTool(activeTool === 'pixelate' ? null : 'pixelate')}
            title="Difuminar / Pixelar"
          >
            <FontAwesomeIcon icon={faThLarge} style={{ fontSize: '1.05rem' }} />
          </button>

          {/* 7. Deshacer */}
          <button
            type="button"
            className="btn btn-sm d-flex align-items-center justify-content-center shadow-none rounded-2 p-2"
            style={{
              width: '38px',
              height: '38px',
              color: historyIndex <= 0 ? 'rgba(174, 186, 193, 0.3)' : '#aebac1',
              cursor: historyIndex <= 0 ? 'not-allowed' : 'pointer'
            }}
            disabled={historyIndex <= 0}
            onClick={handleUndo}
            title="Deshacer"
          >
            <FontAwesomeIcon icon={faUndo} style={{ fontSize: '1.05rem' }} />
          </button>
        </div>

        {/* Derecha: Guardar (Listo), Copiar y Descargar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            className="btn btn-sm px-3 py-1 rounded-pill shadow-none fw-semibold"
            style={{
              backgroundColor: '#00a884',
              color: '#ffffff',
              border: 'none',
              fontSize: '0.82rem',
              cursor: 'pointer'
            }}
            onClick={handleSaveAndClose}
            title="Guardar cambios y volver al chat"
          >
            Listo
          </button>

          <button
            type="button"
            className="btn btn-link p-2 text-decoration-none shadow-none rounded-circle d-flex align-items-center justify-content-center"
            style={{ color: '#aebac1', width: '38px', height: '38px', cursor: 'pointer' }}
            onClick={handleCopy}
            title="Copiar imagen"
          >
            <FontAwesomeIcon icon={faCopy} style={{ fontSize: '1.1rem' }} />
          </button>

          <button
            type="button"
            className="btn btn-link p-2 text-decoration-none shadow-none rounded-circle d-flex align-items-center justify-content-center"
            style={{ color: '#aebac1', width: '38px', height: '38px', cursor: 'pointer' }}
            onClick={handleDownload}
            title="Descargar imagen"
          >
            <FontAwesomeIcon icon={faDownload} style={{ fontSize: '1.1rem' }} />
          </button>
        </div>
      </div>

      {/* ── 2. Barra de Opciones y Herramientas Activas ────────────────────────── */}
      {activeTool && (
        <div
          style={{
            minHeight: '48px',
            backgroundColor: '#182229',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '16px',
            padding: '6px 16px',
            flexShrink: 0
          }}
        >
          {/* Herramienta LÁPIZ */}
          {activeTool === 'pen' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '0.82rem', color: '#8696a0' }}>Color:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {WHATSAPP_COLORS.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      padding: 0,
                      backgroundColor: c.hex,
                      border: strokeColor === c.hex ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)',
                      boxShadow: strokeColor === c.hex ? '0 0 0 2px #00a884' : 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => setStrokeColor(c.hex)}
                    title={c.label}
                  />
                ))}
              </div>

              <div style={{ width: '1px', height: '18px', backgroundColor: 'rgba(255,255,255,0.15)' }} />

              <span style={{ fontSize: '0.82rem', color: '#8696a0' }}>Grosor:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {STROKE_WIDTHS.map(sw => (
                  <button
                    key={sw.id}
                    type="button"
                    className="btn btn-sm px-2 py-0.5 shadow-none rounded-2"
                    style={{
                      fontSize: '0.74rem',
                      fontWeight: 600,
                      backgroundColor: strokeWidth === sw.size ? '#00a884' : 'rgba(255,255,255,0.08)',
                      color: strokeWidth === sw.size ? '#ffffff' : '#aebac1',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => setStrokeWidth(sw.size)}
                  >
                    {sw.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Herramienta TEXTO */}
          {activeTool === 'text' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Escribe el texto a colocar..."
                value={textValue}
                onChange={e => setTextValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleStampText();
                }}
                style={{
                  backgroundColor: '#202c33',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#ffffff',
                  fontSize: '0.86rem',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  outline: 'none',
                  minWidth: '220px'
                }}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {WHATSAPP_COLORS.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      padding: 0,
                      backgroundColor: c.hex,
                      border: strokeColor === c.hex ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)',
                      boxShadow: strokeColor === c.hex ? '0 0 0 2px #00a884' : 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => setStrokeColor(c.hex)}
                    title={c.label}
                  />
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {FONT_SIZES.map(fs => (
                  <button
                    key={fs.id}
                    type="button"
                    className="btn btn-sm px-2 py-0.5 shadow-none rounded-2"
                    style={{
                      fontSize: '0.74rem',
                      fontWeight: 600,
                      backgroundColor: fontSize === fs.size ? '#00a884' : 'rgba(255,255,255,0.08)',
                      color: fontSize === fs.size ? '#ffffff' : '#aebac1',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => setFontSize(fs.size)}
                  >
                    {fs.label}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="btn btn-sm px-3 py-1 rounded-pill shadow-none fw-semibold"
                style={{ backgroundColor: '#00a884', color: '#ffffff', fontSize: '0.80rem', cursor: 'pointer' }}
                onClick={handleStampText}
              >
                Agregar a la imagen
              </button>
            </div>
          )}

          {/* Herramienta FORMAS / RECTÁNGULO */}
          {activeTool === 'square' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '0.82rem', color: '#8696a0' }}>
                Arrastra sobre la imagen para dibujar:
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {WHATSAPP_COLORS.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      padding: 0,
                      backgroundColor: c.hex,
                      border: strokeColor === c.hex ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)',
                      boxShadow: strokeColor === c.hex ? '0 0 0 2px #00a884' : 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => setStrokeColor(c.hex)}
                    title={c.label}
                  />
                ))}
              </div>

              <button
                type="button"
                className="btn btn-sm px-2.5 py-0.5 shadow-none rounded-2"
                style={{
                  fontSize: '0.76rem',
                  backgroundColor: shapeFilled ? '#00a884' : 'rgba(255,255,255,0.08)',
                  color: shapeFilled ? '#ffffff' : '#aebac1',
                  border: 'none',
                  cursor: 'pointer'
                }}
                onClick={() => setShapeFilled(!shapeFilled)}
              >
                {shapeFilled ? 'Relleno: Sí' : 'Relleno: No'}
              </button>
            </div>
          )}

          {/* Herramienta RECORTAR Y ROTAR */}
          {activeTool === 'crop' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '0.82rem', color: '#8696a0' }}>
                Arrastra el marco sobre la imagen para recortar.
              </span>

              <button
                type="button"
                className="btn btn-sm px-2.5 py-1 shadow-none rounded-2 d-flex align-items-center gap-1.5"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  color: '#e9edef',
                  fontSize: '0.80rem',
                  border: 'none',
                  cursor: 'pointer'
                }}
                onClick={handleRotate90}
                title="Girar 90° a la derecha"
              >
                <FontAwesomeIcon icon={faRotateRight} />
                <span>Rotar 90°</span>
              </button>

              {cropSelection && cropSelection.width > 15 && cropSelection.height > 15 && (
                <button
                  type="button"
                  className="btn btn-sm px-3 py-1 rounded-pill shadow-none fw-semibold d-flex align-items-center gap-1.5"
                  style={{ backgroundColor: '#00a884', color: '#ffffff', fontSize: '0.80rem', border: 'none', cursor: 'pointer' }}
                  onClick={handleApplyCrop}
                >
                  <FontAwesomeIcon icon={faCheck} />
                  <span>Aplicar Recorte</span>
                </button>
              )}
            </div>
          )}

          {/* Herramienta DIFUMINAR / PIXELAR */}
          {activeTool === 'pixelate' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.84rem', color: '#e9edef' }}>
                🛡️ <strong>Arrastra un recuadro</strong> sobre datos sensibles (rostros, contraseñas o tarjetas) para pixelarlos al instante.
              </span>
            </div>
          )}

          {/* Herramienta FILTROS */}
          {activeTool === 'filter' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.82rem', color: '#8696a0' }}>Filtro:</span>
              {FILTERS.map(f => (
                <button
                  key={f.id}
                  type="button"
                  className="btn btn-sm px-3 py-0.5 shadow-none rounded-pill"
                  style={{
                    fontSize: '0.78rem',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: '#e9edef',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleApplyFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 3. Zona Central del Canvas Interactivo ────────────────────────────── */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#0c1317',
          minHeight: 0
        }}
      >
        <div
          style={{
            position: 'relative',
            display: 'inline-block',
            maxWidth: '100%',
            maxHeight: '100%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
            borderRadius: '6px',
            overflow: 'hidden'
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              display: imageLoaded ? 'block' : 'none',
              maxWidth: '92%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              margin: '0 auto',
              cursor: activeTool === 'crop' || activeTool === 'pixelate' || activeTool === 'square' || activeTool === 'pen'
                ? 'crosshair'
                : 'default'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseUp}
          />

          {!imageLoaded && (
            <div style={{ padding: '60px 80px', color: '#8696a0', fontSize: '0.95rem' }}>
              Cargando imagen...
            </div>
          )}

          {/* Recuadro visual mientras se recorta */}
          {activeTool === 'crop' && cropSelection && cropSelection.width > 5 && (
            <div
              style={{
                position: 'absolute',
                ...getScreenSelectionStyle(cropSelection),
                border: '2px dashed #00a884',
                backgroundColor: 'rgba(0, 168, 132, 0.15)',
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
                pointerEvents: 'none'
              }}
            />
          )}

          {/* Recuadro visual mientras se pixela */}
          {activeTool === 'pixelate' && pixelateSelection && pixelateSelection.width > 5 && (
            <div
              style={{
                position: 'absolute',
                ...getScreenSelectionStyle(pixelateSelection),
                border: '2px dotted #ef4444',
                backgroundColor: 'rgba(239, 68, 68, 0.20)',
                pointerEvents: 'none'
              }}
            />
          )}

          {/* Recuadro visual mientras se dibuja rectángulo */}
          {activeTool === 'square' && shapeSelection && shapeSelection.width > 5 && (
            <div
              style={{
                position: 'absolute',
                ...getScreenSelectionStyle(shapeSelection),
                border: `2px solid ${strokeColor}`,
                backgroundColor: shapeFilled ? strokeColor : 'transparent',
                pointerEvents: 'none'
              }}
            />
          )}
        </div>
      </div>

      {/* ── 4. Barra Inferior Estilo WhatsApp Web (Pie de foto, Miniaturas y Enviar) ── */}
      <div
        style={{
          backgroundColor: '#0c1317',
          padding: '10px 20px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          flexShrink: 0
        }}
      >
        {/* Input centrado para escribir pie de foto */}
        <div
          style={{
            maxWidth: '640px',
            width: '92%',
            backgroundColor: '#202c33',
            borderRadius: '8px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            marginBottom: '12px',
            border: '1px solid rgba(255, 255, 255, 0.06)'
          }}
        >
          <input
            ref={captionInputRef}
            type="text"
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#e9edef',
              fontSize: '0.94rem',
              width: '100%'
            }}
            placeholder="Escribe un mensaje"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendAndClose();
              }
            }}
          />

          <button
            type="button"
            className="btn btn-link p-1 text-decoration-none shadow-none ms-2"
            style={{ color: '#8696a0', cursor: 'pointer' }}
            onClick={() => setShowEmojiTray(!showEmojiTray)}
            title="Añadir emoji"
          >
            <FontAwesomeIcon icon={faSmile} style={{ fontSize: '1.25rem' }} />
          </button>

          {/* Popover de emojis para el pie de foto usando el componente del chat */}
          {showEmojiTray && (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                right: '0',
                marginBottom: '10px',
                zIndex: 1060
              }}
            >
              <EmojiPickerPopover
                onSelectEmoji={emoji => {
                  setCaption(prev => prev + emoji);
                  captionInputRef.current?.focus();
                }}
                onClose={() => setShowEmojiTray(false)}
                isDark={true}
              />
            </div>
          )}
        </div>

        {/* Tira inferior de miniaturas centrada con botón '+' */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          {/* Miniatura de la imagen activa con borde verde WhatsApp */}
          <div
            style={{
              width: '48px',
              height: '48px',
              border: '2.5px solid #00a884',
              borderRadius: '6px',
              overflow: 'hidden',
              cursor: 'pointer'
            }}
            title={filePreview?.name || 'Imagen actual'}
          >
            <img
              src={filePreview?.url}
              alt="Thumbnail"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>

          {/* Botón '+' para agregar / cambiar imagen */}
          <button
            type="button"
            style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#202c33',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              color: '#8696a0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onClick={() => {
              if (onAddFile) onAddFile();
            }}
            title="Seleccionar otra imagen"
          >
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: '1.1rem' }} />
          </button>
        </div>

        {/* Botón circular verde de Enviar flotante a la derecha estilo WhatsApp */}
        <div style={{ position: 'absolute', right: '28px', bottom: '16px', zIndex: 20 }}>
          <button
            type="button"
            style={{
              width: '54px',
              height: '54px',
              borderRadius: '50%',
              backgroundColor: '#00a884',
              color: '#ffffff',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(0, 168, 132, 0.4)',
              cursor: 'pointer',
              transition: 'transform 0.15s ease, background-color 0.15s ease'
            }}
            onClick={handleSendAndClose}
            title="Enviar imagen (Enter)"
          >
            <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: '1.25rem', marginLeft: '-2px' }} />
          </button>
        </div>
      </div>

      {/* ── 5. Toast Pequeño de Imagen Copiada ────────────────────────────────── */}
      {showCopyToast && (
        <div
          style={{
            position: 'absolute',
            bottom: '90px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#202c33',
            border: '1px solid #00a884',
            color: '#e9edef',
            padding: '8px 20px',
            borderRadius: '20px',
            fontSize: '0.84rem',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)'
          }}
        >
          <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#00a884' }} />
          <span>Imagen copiada al portapapeles</span>
        </div>
      )}

      {/* ── 6. Alerta de Confirmación de Descarte estilo WhatsApp ─────────────── */}
      {showDiscardConfirm && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.72)',
            backdropFilter: 'blur(3px)',
            zIndex: 100000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
        >
          <div
            style={{
              backgroundColor: '#233138',
              maxWidth: '420px',
              width: '100%',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)'
            }}
          >
            <h6 style={{ fontSize: '1.15rem', fontWeight: 600, color: '#ffffff', marginBottom: '8px' }}>
              ¿Descartar cambios?
            </h6>
            <p style={{ color: '#8696a0', fontSize: '0.90rem', lineHeight: '1.45', marginBottom: '24px' }}>
              Si sales ahora, se perderán todas las ediciones que hiciste a la imagen.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-sm px-3 py-1.5 rounded-pill shadow-none fw-semibold"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(239, 68, 68, 0.5)',
                  color: '#ef4444',
                  fontSize: '0.84rem',
                  cursor: 'pointer'
                }}
                onClick={handleConfirmDiscard}
              >
                Descartar
              </button>
              <button
                type="button"
                className="btn btn-sm px-3.5 py-1.5 rounded-pill shadow-none fw-semibold"
                style={{
                  backgroundColor: '#00a884',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '0.84rem',
                  cursor: 'pointer'
                }}
                onClick={() => setShowDiscardConfirm(false)}
              >
                Continuar editando
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return chatContainer ? createPortal(modalContent, chatContainer) : modalContent;
};

ImageEditorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  filePreview: PropTypes.object,
  inputText: PropTypes.string,
  setInputText: PropTypes.func,
  onSave: PropTypes.func,
  onSend: PropTypes.func,
  onDiscard: PropTypes.func,
  onClose: PropTypes.func,
  onAddFile: PropTypes.func
};

export default ImageEditorModal;
